/* eslint-disable no-underscore-dangle */
/**
 * Weir-Orifice Flow Capacity Calculator & SVG Chart
 *
 * Implements the weir-orifice-conditional-v1 hydraulic algorithm:
 *   B = 3.3 * perimeter_ft * (depth ^ 1.5)           [weir flow, cfs]
 *   C = 0.6 * open_area_sf * sqrt(2 * 32.2 * depth)   [orifice flow, cfs]
 *   ratio = ABS(B - C) / MIN(B, C)
 *   flow = ratio > 0.23 ? MIN(B, C) : 0.8 * MIN(B, C)
 */

const G = 32.2;
const WEIR_COEFF = 3.3;
const ORIFICE_COEFF = 0.6;
const RATIO_THRESHOLD = 0.23;
const BLEND_FACTOR = 0.8;

export function computeFlowCurve(
    openAreaSf,
    perimeterFt,
    depthMin = 0.01,
    depthMax = 2.0,
    depthStep = 0.04,
) {
    const points = [];
    const min = Math.max(0.001, Number(depthMin) || 0.01);
    const max = Math.max(min, Number(depthMax) || 2.0);
    const step = Math.max(0.005, Number(depthStep) || 0.04);

    for (let d = min; d <= max + 1e-7; d += step) {
        const depth = Math.round(d * 1000) / 1000;
        const b = WEIR_COEFF * perimeterFt * (depth ** 1.5);
        const c = ORIFICE_COEFF * openAreaSf * Math.sqrt(2 * G * depth);
        const minBc = Math.min(b, c);

        let flow = 0.0;
        if (minBc > 0) {
            const ratio = Math.abs(b - c) / minBc;
            flow = ratio > RATIO_THRESHOLD ? minBc : BLEND_FACTOR * minBc;
        }

        points.push({
            depth,
            flow: Math.round(flow * 100) / 100,
        });
    }

    return points;
}

export default function WeirFlowChart(node, params = {}) {
    const flowData = params.flowData || JSON.parse(node.getAttribute('data-flow-data') || '{}');
    const grates = Object.keys(flowData);
    if (!grates.length) {
        return;
    }

    const canvas = node.querySelector('.weir-flow-chart__canvas');
    if (!canvas) {
        return;
    }

    const grateButtons = node.querySelectorAll('.weir-flow-chart__grate-btn');
    let currentGrate = grates[0];

    // Chart Dimensions
    const VB_W = 680;
    const VB_H = 320;
    const PAD_T = 30;
    const PAD_B = 50;
    const PAD_L = 60;
    const PAD_R = 25;
    const PLOT_W = VB_W - PAD_L - PAD_R;
    const PLOT_H = VB_H - PAD_T - PAD_B;

    function render(grateKey) {
        const item = flowData[grateKey];
        if (!item || !item.openArea || !item.perimeter) {
            return;
        }

        const openArea = parseFloat(item.openArea);
        const perimeter = parseFloat(item.perimeter);
        const points = computeFlowCurve(openArea, perimeter);
        if (!points.length) {
            return;
        }

        const maxFlowData = Math.max(...points.map(p => p.flow));
        const maxFlow = Math.max(1, maxFlowData * 1.1);

        const mapX = d => PAD_L + (d / 2.0) * PLOT_W;
        const mapY = f => PAD_T + PLOT_H - (f / maxFlow) * PLOT_H;

        // Grid lines - Horizontal (Y: Flow)
        let gridH = '';
        for (let i = 0; i <= 5; i += 1) {
            const flowVal = (maxFlow * (5 - i)) / 5;
            const y = PAD_T + (PLOT_H * i) / 5;
            const yFormatted = flowVal >= 10 ? flowVal.toFixed(0) : flowVal.toFixed(1);
            gridH += `
                <line x1="${PAD_L}" y1="${y}" x2="${PAD_L + PLOT_W}" y2="${y}" stroke="#EDEDED" stroke-width="1" />
                <text x="${PAD_L - 8}" y="${y + 4}" font-size="10" fill="#686B73" text-anchor="end" font-family="sans-serif">${yFormatted}</text>
            `;
        }

        // Grid lines - Vertical (X: Depth 0 to 2 ft)
        let gridV = '';
        for (let i = 0; i <= 5; i += 1) {
            const depthVal = (2.0 * i) / 5;
            const x = PAD_L + (PLOT_W * i) / 5;
            gridV += `
                <line x1="${x}" y1="${PAD_T}" x2="${x}" y2="${PAD_T + PLOT_H}" stroke="#EDEDED" stroke-width="1" />
                <text x="${x}" y="${PAD_T + PLOT_H + 18}" font-size="10" fill="#686B73" text-anchor="middle" font-family="sans-serif">${depthVal.toFixed(1)}</text>
            `;
        }

        // Path / Polyline coordinates
        const polylinePoints = points
            .map(p => `${mapX(p.depth).toFixed(1)},${mapY(p.flow).toFixed(1)}`)
            .join(' ');

        // Interactive Dots with native tooltips
        let dataDots = '';
        points.forEach(p => {
            const cx = mapX(p.depth).toFixed(1);
            const cy = mapY(p.flow).toFixed(1);
            const label = `Depth: ${p.depth.toFixed(2)} ft, Flow: ${p.flow.toFixed(2)} cfs`;
            dataDots += `
                <g class="weir-flow-chart__point">
                    <circle cx="${cx}" cy="${cy}" r="3" fill="#FFF" stroke="#1F2128" stroke-width="2" />
                    <circle cx="${cx}" cy="${cy}" r="10" fill="transparent" cursor="pointer">
                        <title>${label}</title>
                    </circle>
                </g>
            `;
        });

        canvas.innerHTML = `
            <svg viewBox="0 0 ${VB_W} ${VB_H}" role="img" aria-label="Weir Flow Capacity Chart for Grate ${grateKey}">
                <rect x="0" y="0" width="${VB_W}" height="${VB_H}" fill="#FFF" />
                <rect x="${PAD_L}" y="${PAD_T}" width="${PLOT_W}" height="${PLOT_H}" fill="#F8F9FA" stroke="#D6D7DB" stroke-width="1" />
                ${gridH}
                ${gridV}
                <polyline points="${polylinePoints}" fill="none" stroke="#1F2128" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
                ${dataDots}
                <text x="${PAD_L + PLOT_W / 2}" y="${VB_H - 10}" font-size="11" font-weight="600" fill="#1F2128" text-anchor="middle" font-family="sans-serif">Depth of Water Above Grate (ft)</text>
                <text x="16" y="${PAD_T + PLOT_H / 2}" font-size="11" font-weight="600" fill="#1F2128" text-anchor="middle" font-family="sans-serif" transform="rotate(-90 16 ${PAD_T + PLOT_H / 2})">Grate Inlet Flow Rate (cfs)</text>
            </svg>
        `;
    }

    // Bind grate pill toggles
    grateButtons.forEach(btn => {
        btn.addEventListener('click', () => {
            const grateKey = btn.getAttribute('data-grate-key');
            if (!grateKey || grateKey === currentGrate) {
                return;
            }

            grateButtons.forEach(b => {
                b.classList.remove('is-active');
                b.setAttribute('aria-pressed', 'false');
            });

            btn.classList.add('is-active');
            btn.setAttribute('aria-pressed', 'true');
            currentGrate = grateKey;
            render(currentGrate);

            // Update parent dialog's download button and title if inside dialog
            const panel = node.closest('.infrastructure-product-dialog__panel');
            if (panel) {
                const exportBtn = panel.querySelector('[data-wfc-export]');
                if (exportBtn) {
                    exportBtn.setAttribute('data-grate-type', currentGrate);
                }
                const flowTitle = panel.querySelector('.infrastructure-product-dialog__flow-title');
                if (flowTitle) {
                    flowTitle.textContent = `Flow Capacity Chart - Grate ${currentGrate}`;
                }
            }
        });
    });

    // Expose _wfcInstance for PDF export
    node._wfcInstance = {
        exportSVG: () => {
            const svg = canvas.querySelector('svg');
            if (!svg) return null;
            const clone = svg.cloneNode(true);
            clone.querySelectorAll('.wfc__line, line, polyline').forEach(el => {
                if (el.style.opacity === '0') el.style.opacity = '1';
            });
            return clone;
        },
        getCurrentGrate: () => currentGrate,
        getCatalogNumber: () => params.catalogNumber || node.getAttribute('data-catalog-number') || '',
    };

    // Initial render
    render(currentGrate);
}
